/**
 * Theme Spacing Patch
 * 
 * This patch ensures that theme.spacing is always available in styled-components,
 * even if the theme hasn't fully loaded or is undefined.
 */

import { DefaultTheme } from 'styled-components';

// Default spacing values to use as fallback
const defaultSpacing = {
  '0': '0',
  '1': '1px',
  '2': '2px',
  '4': '4px',
  '8': '8px',
  '12': '12px',
  '16': '16px',
  '20': '20px',
  '24': '24px',
  '32': '32px',
  '40': '40px',
  '48': '48px',
  '56': '56px',
  '64': '64px',
  '80': '80px',
  '96': '96px',
  '128': '128px',
  xs: '4px',
  sm: '8px',
  md: '16px',
  lg: '24px',
  xl: '32px',
  '2xl': '48px',
  '3xl': '64px',
  '4xl': '96px',
  '5xl': '128px',
};

// Patch the styled-components theme
if (typeof window !== 'undefined') {
  const originalStyled = (window as any).styled;
  
  if (originalStyled) {
    // Intercept theme access to ensure spacing is always available
    const proxyHandler = {
      get(target: any, prop: string) {
        if (prop === 'spacing' && (!target || !target.spacing)) {
          console.warn('[Theme Spacing Patch] Theme.spacing was undefined, using defaults');
          return defaultSpacing;
        }
        return target ? target[prop] : undefined;
      }
    };
    
    // Store original theme getter if it exists
    const ThemeContext = originalStyled.ThemeContext;
    if (ThemeContext && ThemeContext._currentValue) {
      const originalTheme = ThemeContext._currentValue;
      if (originalTheme && typeof originalTheme === 'object') {
        ThemeContext._currentValue = new Proxy(originalTheme, proxyHandler);
      }
    }
  }
}

/**
 * Helper function to safely get spacing value
 */
export const getSpacing = (theme: DefaultTheme | undefined, key: string): string => {
  const fallback = defaultSpacing[key as keyof typeof defaultSpacing] || '16px';
  
  if (!theme || !theme.spacing) {
    return fallback;
  }
  
  return theme.spacing[key] || fallback;
};

export { defaultSpacing };